import { useMemo, type ReactNode, type Ref } from "react";
import classNames from "classnames";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { useCommandIslandPresence } from "./useCommandIslandPresence";
import styles from "./CommandIsland.module.scss";

interface CommandIslandProps {
    children: ReactNode;
    alignEnd?: boolean;
    sticky?: boolean;
    /**
     * Shows or hides the island with its exit motion, which a conditionally
     * rendered island can't play. Omit it to render the island whenever it's
     * mounted; it rises in either way.
     */
    isOpen?: boolean;
    className?: string;
}

/**
 * Floating bar for a page's or a selection's actions. It rises in when it
 * appears and, with `isOpen`, sinks out faster when it closes.
 */
export const CommandIsland = ({
    children,
    alignEnd,
    className,
    sticky,
    isOpen = true,
    ref,
}: CommandIslandProps & { ref?: Ref<HTMLDivElement> }) => {
    const { nodeRef, isMounted, shownChildren } = useCommandIslandPresence(
        isOpen,
        children,
    );
    const mergedRef = useMemo(
        () => mergeRefs<HTMLDivElement>([ref, nodeRef]),
        [ref, nodeRef],
    );
    if (!isMounted) {
        return null;
    }
    return (
        <div
            ref={mergedRef}
            className={classNames(styles.commandIsland, className, {
                [styles.alignEnd]: alignEnd,
                [styles.sticky]: sticky,
            })}
            data-state={isOpen ? "open" : "closed"}
        >
            {shownChildren}
        </div>
    );
};
